import type { ForecastParameters } from "@common/client"
import { z } from "zod"
import type { Display } from "@/components/layout/display-options"
import {
  BASES,
  DEFAULT_PERIOD_DAYS,
  periodFromToday,
} from "@/routes/_layout/forecasts/-lib/forecasts"
import {
  type Column,
  columnSchema,
  DEFAULT_COLUMNS,
  withFixed,
} from "./columns"
import { APPLICABILITIES } from "./rows"

export const GROUPINGS = ["window", "aircraft", "programme", "type"] as const

export const AIRCRAFT_GROUPINGS = GROUPINGS.filter(
  (grouping) => grouping !== "aircraft",
)

const groupingSchema = z.enum(GROUPINGS)

export type Grouping = z.infer<typeof groupingSchema>

export const GROUPING_LABELS: Record<Grouping, string> = {
  window: "Due window",
  aircraft: "Aircraft",
  programme: "Program",
  type: "Type",
}

export const ROW_STATUSES = [
  "overdue",
  "in_tolerance",
  "due_soon",
  "scheduled",
  "undetermined",
  "excluded",
] as const

const directionSchema = z.enum(["asc", "desc"])

export type Direction = z.infer<typeof directionSchema>

export const dueSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  aircraft: z.array(z.string()).optional().catch(undefined),
  programme: z.array(z.string()).optional().catch(undefined),
  type: z.array(z.string()).optional().catch(undefined),
  status: z.array(z.enum(ROW_STATUSES)).optional().catch(undefined),
  applicability: z.enum(APPLICABILITIES).optional().catch(undefined),
  group: z
    .enum([...GROUPINGS, "none"])
    .optional()
    .catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: columnSchema.optional().catch(undefined),
  direction: directionSchema.optional().catch(undefined),
  basis: z.enum(BASES).optional().catch(undefined),
  item: z.string().optional().catch(undefined),
  exclude: z.string().optional().catch(undefined),
})

export type DueSearch = z.infer<typeof dueSearchSchema>

export const previewSearchSchema = dueSearchSchema.extend({
  aircraft_ids: z.array(z.string()).min(1).optional().catch(undefined),
  starts_on: z
    .string()
    .date()
    .catch(() => periodFromToday(DEFAULT_PERIOD_DAYS).starts_on),
  ends_on: z
    .string()
    .date()
    .catch(() => periodFromToday(DEFAULT_PERIOD_DAYS).ends_on),
  name: z.string().catch(""),
  note: z.string().optional().catch(undefined),
})

export type PreviewSearch = z.infer<typeof previewSearchSchema>

export type ForecastDraft = {
  parameters: ForecastParameters
  name: string
  note: string
}

export type DueDisplay = {
  group: Grouping | undefined
  subgroup: Grouping | undefined
  columns: Column[]
  sort: Column
  direction: Direction
}

export function displayOf(search: DueSearch): DueDisplay {
  const group = search.group === "none" ? undefined : (search.group ?? "window")
  return {
    group,
    subgroup: group && search.subgroup !== group ? search.subgroup : undefined,
    columns: search.columns ? withFixed(search.columns) : DEFAULT_COLUMNS,
    sort: search.sort ?? "due",
    direction: search.direction ?? "asc",
  }
}

export function limitDisplay(
  display: DueDisplay,
  columns: readonly Column[],
  groupings: readonly Grouping[],
): DueDisplay {
  const allowed = (grouping: Grouping | undefined) =>
    grouping && groupings.includes(grouping) ? grouping : undefined
  const group = display.group && (allowed(display.group) ?? "window")
  const subgroup = allowed(display.subgroup)
  const sorted = columns.includes(display.sort)
  return {
    group,
    subgroup: subgroup === group ? undefined : subgroup,
    columns: display.columns.filter((column) => columns.includes(column)),
    sort: sorted ? display.sort : "due",
    direction: sorted ? display.direction : "asc",
  }
}

export function groupingSearch(next: Display<Grouping>): Partial<DueSearch> {
  const group = next.group === undefined ? "none" : next.group
  return {
    group: group === "window" ? undefined : group,
    subgroup: next.subgroup,
  }
}

export function sortSearch(
  display: DueDisplay,
  column: Column,
): Partial<DueSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "due" && direction === "asc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

export function columnsSearch(
  columns: Column[],
  available: readonly Column[],
): Partial<DueSearch> {
  const defaults =
    columns.length === available.length &&
    columns.every((column) => available.includes(column))
  return { columns: defaults ? undefined : columns }
}

export function previewSearch({
  parameters,
  name,
  note,
}: ForecastDraft): PreviewSearch {
  return {
    aircraft_ids:
      parameters.scope.kind === "aircraft"
        ? parameters.scope.aircraft_ids
        : undefined,
    starts_on: parameters.period.starts_on,
    ends_on: parameters.period.ends_on,
    basis: parameters.basis,
    name,
    note: note || undefined,
  }
}

export function previewParameters({
  aircraft_ids,
  starts_on,
  ends_on,
  basis,
}: Pick<
  PreviewSearch,
  "aircraft_ids" | "starts_on" | "ends_on" | "basis"
>): ForecastParameters {
  return {
    scope: aircraft_ids
      ? { kind: "aircraft", aircraft_ids }
      : { kind: "fleet" },
    period: { starts_on, ends_on },
    basis: basis ?? "average_utilization",
  }
}
